// Ionic Loading is functionally  broken, but visually it is working, so we need to manage the loading state manually

import { IonLoading, useIonLoading } from "@ionic/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { ONE_SECOND } from "@/utils/constants/time";

type LoadingRequests = {
  id: string;
  maxTimeout: number;
  args: Parameters<ReturnType<typeof useIonLoading>[0]>;
}[];

type PresentLodingProps = [
  number,
  ...Parameters<ReturnType<typeof useIonLoading>[0]>
];

const LoadingContext = createContext<{
  presentLoading: (...props: PresentLodingProps) => {
    dismiss: () => void;
  };
  dismissAllLoading: () => void;
}>({
  // eslint-disable-next-line no-empty-function
  dismissAllLoading: () => {},
  // eslint-disable-next-line no-empty-function
  presentLoading: () => ({ dismiss: () => {} }),
});

function LoadingProvider({ children }: { children: React.ReactNode }) {
  const [dismissable, setDismissable] = useState<Date | null>(null);
  const [showLoading, setShowLoading] = useState(false);
  const [requests, setRequests] = useState<LoadingRequests>([]);

  const activeRequest = requests.length ? requests[requests.length - 1] : null;
  const generateUniqueId = useCallback((): string => {
    // Prefer a UUID if available; fall back to a timestamp+random string.
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
    // eslint-disable-next-line no-magic-numbers
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }, []);

  // Dismiss *a* loading request
  const dismissLoading = useCallback((id: string) => {
    setRequests((draft) => draft.filter((request) => request.id !== id));
  }, []);
  // Setting up the request queue
  const presentLoading = useCallback(
    (...props: PresentLodingProps) => {
      const [maxTimeout, ...args] = props;
      const id = generateUniqueId();
      setRequests((draft) => [
        ...draft,
        { args, id, maxTimeout: Date.now() + maxTimeout },
      ]);
      return {
        dismiss: () => dismissLoading(id),
      };
    },
    [dismissLoading, generateUniqueId]
  );

  const dismissAllLoading = useCallback(() => setRequests([]), []);
  const contextValue = useMemo(
    () => ({ dismissAllLoading, presentLoading }),
    [dismissAllLoading, presentLoading]
  );

  //   watch the request queue, and show loading when there is a request and the loading is not already showing
  useEffect(() => {
    if (requests.length && !showLoading) {
      setShowLoading(true);
      setDismissable(new Date(Date.now() + ONE_SECOND));
    }
  }, [requests, showLoading]);

  //   watch the request queue, and dismiss loading when there is no request and the loading is showing
  useEffect(() => {
    const interval = setInterval(() => {
      if (
        !requests.length &&
        showLoading &&
        dismissable &&
        dismissable < new Date()
      ) {
        setShowLoading(() => false);
        setDismissable(() => null);
      }
    }, ONE_SECOND);
    return () => clearInterval(interval);
  }, [requests, showLoading, dismissable]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (requests.length) {
        const filteredRequests = requests.filter(
          (request) => request.maxTimeout > Date.now()
        );
        if (filteredRequests.length !== requests.length) {
          setRequests(filteredRequests);
        }
      }
    }, ONE_SECOND);
    return () => clearInterval(interval);
  }, [requests]);

  return (
    <LoadingContext.Provider value={contextValue}>
      <IonLoading isOpen={showLoading} {...activeRequest?.args} />
      {children}
    </LoadingContext.Provider>
  );
}

function useLoadingPage() {
  return useContext(LoadingContext);
}

export { LoadingProvider, useLoadingPage };
